import { useSyncExternalStore } from 'react';
import type { Dialect, SpellingIssue } from '@realmery/realm-core';
// The desktop's own spelling dictionary, exported for the web (schema/spelling/README.md).
import spellingUrl from '../../../../schema/spelling/en-spelling.tsv.gz?url';
import type { SpellingRequest } from './spelling.worker.ts';

/**
 * The page's side of spelling (docs/PARITY-STEP-6-PLAN.md section 6): its worker, started
 * the first time a paragraph is checked; your own dictionary ("Add to My Dictionary"),
 * kept in this browser for every realm, as the desktop keeps it beside the app; and a
 * counter that changes whenever a dictionary or an ignore does, so editors check again.
 */

let worker: Worker | null = null;
let next = 1;
const waiting = new Map<number, { resolve: (value: SpellingIssue[][]) => void; reject: (e: Error) => void }>();

export function checkSpelling(paragraphs: readonly string[], dialect: Dialect): Promise<SpellingIssue[][]> {
  if (!worker) {
    worker = new Worker(new URL('./spelling.worker.ts', import.meta.url), { type: 'module', name: 'spelling' });
    worker.onmessage = (event: MessageEvent<{ id: number; result?: SpellingIssue[][]; error?: string }>) => {
      const { id, result, error } = event.data;
      const pending = waiting.get(id);
      waiting.delete(id);
      if (error !== undefined) pending?.reject(new Error(error));
      else pending?.resolve(result!);
    };
  }
  const id = next++;
  return new Promise((resolve, reject) => {
    waiting.set(id, { resolve, reject });
    worker!.postMessage({ id, url: spellingUrl, dialect, paragraphs } satisfies SpellingRequest);
  });
}

// ---- what's changed: dictionaries and ignores ----

let version = 0;
const listeners = new Set<() => void>();

/** Bumped whenever a dictionary or an ignore changes, so every editor checks again. */
export function spellingChanged(): void {
  version++;
  for (const l of listeners) l();
}

export function useSpellingVersion(): number {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => version,
  );
}

// ---- my dictionary, in this browser ----

const MY_DICTIONARY = 'realmery.dictionary';

export function myDictionary(): string[] {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(MY_DICTIONARY) ?? '[]');
    return Array.isArray(parsed) ? parsed.filter((w): w is string => typeof w === 'string' && w.trim() !== '' && w.length <= 200) : [];
  } catch {
    return [];
  }
}

function saveMyDictionary(words: readonly string[]): void {
  try {
    localStorage.setItem(MY_DICTIONARY, JSON.stringify([...words].sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))));
  } catch {
    // Private browsing: it's only for this visit.
  }
  spellingChanged();
}

export function addToMyDictionary(word: string): void {
  const clean = word.trim();
  if (clean === '') return;
  const words = myDictionary();
  if (!words.some((w) => w.toLowerCase() === clean.toLowerCase())) saveMyDictionary([...words, clean]);
}

export function removeFromMyDictionary(word: string): void {
  saveMyDictionary(myDictionary().filter((w) => w.toLowerCase() !== word.trim().toLowerCase()));
}
